/**
 * ログイン（メール → 確認コード）。手順・文言の正は `@kalori/identity/business-login`（3 線共通）。
 *
 * ⚠️ 「このメールは登録されていません」とは言わない — サーバは登録の有無で応答を変えない（列挙防止）。
 *    画面も同じく、送信後は常に「届いたコードを入力」に進む。
 */
import { useState, type FormEvent } from "react";
import { LOGIN_CODE_RESEND_LIMIT_NOTE } from "@kalori/identity/business-login";
import { api, type Me } from "../api";
import { Link } from "../router";
import { OnboardingLayout, FieldLabel } from "./Onboarding";

export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    const r = await api.sendCode(email.trim());
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setStep("code");
    setCode("");
    setNotice(e ? null : "確認コードを送り直しました。");
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await api.verifyCode(email.trim(), code);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onSignedIn(r.data);
  };

  return (
    <OnboardingLayout title={step === "email" ? "ログイン" : "確認コードの入力"}>
      <p className="b2b-lead">{step === "email" ? "ご登録のメールアドレスに、6 桁の確認コードをお送りします（パスワードはありません）。" : <><strong>{email}</strong> 宛に確認コードを送りました。メールに届いた 6 桁の数字を入力してください（10 分間有効）。</>}</p>
      {step === "email" ? (
        <form key="email" onSubmit={send} className="b2b-form b2b-stack">
          <label className="b2b-field">
            <FieldLabel label="メールアドレス" required />
            <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </label>
          {error && <p className="b2b-error" role="alert">{error}</p>}
          <button className="b2b-button" disabled={busy || !email.trim()}>
            {busy ? "送信しています…" : "確認コードを送る"}
          </button>
        </form>
      ) : (
        <form key="code" onSubmit={verify} className="b2b-form b2b-stack">
          <label className="b2b-field">
            <FieldLabel label="確認コード" required />
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 -]*"
              maxLength={9}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              className="b2b-code"
            />
          </label>
          {error && <p className="b2b-error" role="alert">{error}</p>}
          {notice && !error && <p className="b2b-notice" role="status">{notice}</p>}
          <button className="b2b-button" disabled={busy || code.replace(/\D/g, "").length !== 6}>
            {busy ? "確認しています…" : "ログイン"}
          </button>
          <div className="b2b-actions">
            <button type="button" className="b2b-link-button" onClick={() => setStep("email")}>
              メールアドレスを変える
            </button>
            <button type="button" className="b2b-link-button" disabled={busy} onClick={() => void send()}>
              コードを送り直す
            </button>
          </div>
          <p className="b2b-note">
            届かない場合は、迷惑メールのフォルダもご確認ください。{LOGIN_CODE_RESEND_LIMIT_NOTE}
            ご登録の無いメールアドレスにはお送りしていません。
          </p>
        </form>
      )}
      {step === "email" && <p className="b2b-after">はじめての方は <Link href="/apply">利用を申請する</Link>（承認後に招待メールが届きます）。</p>}
    </OnboardingLayout>
  );
}
